// The share card: a 1200x630 picture of someone's receipt and grade, drawn in
// the visitor's browser when they press Share. Nothing is rendered on a
// server, so it costs nothing however many people share.
import { monotonePath } from './curve.js';
import { cycleShort } from './election.js';
import { GRADE_SCOPE, GRAPH_GRADIENT, PARTS, gradeInfo } from './grades.js';
import { usdShort } from './people.js';

const W = 1200;
const H = 630;
const DISPLAY = '"Archivo Variable", "Arial Narrow", Arial, sans-serif';
const MONO = '"IBM Plex Mono", Menlo, monospace';
const BODY = '"Public Sans Variable", system-ui, sans-serif';

async function fontsReady() {
  try {
    await Promise.all([
      document.fonts.load(`900 100px ${DISPLAY}`),
      document.fonts.load(`800 20px ${DISPLAY}`),
      document.fonts.load(`600 20px ${MONO}`),
      document.fonts.load(`400 20px ${MONO}`),
      document.fonts.load(`400 24px ${BODY}`),
    ]);
  } catch {
    // Fallback fonts still make a readable card
  }
}

function stretch(x, value) {
  if ('fontStretch' in x) {
    x.fontStretch = value;
  }
}

// Greedy word wrap; returns the lines
function wrap(x, text, width) {
  const lines = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (x.measureText(next).width > width && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) {
    lines.push(line);
  }
  return lines;
}

function fitText(x, text, { width, maxLines, start, min, font }) {
  for (let size = start; size >= min; size -= 4) {
    x.font = font(size);
    const lines = wrap(x, text, width);
    if (lines.length <= maxLines && lines.every(l => x.measureText(l).width <= width)) {
      return { size, lines };
    }
  }
  // Never drop words: at the smallest size, take as many lines as it needs
  x.font = font(min);
  return { size: min, lines: wrap(x, text, width) };
}

function dotted(x, x0, x1, y) {
  x.save();
  x.strokeStyle = '#A9A5B0';
  x.setLineDash([1.5, 3]);
  x.beginPath();
  x.moveTo(x0, y);
  x.lineTo(x1, y);
  x.stroke();
  x.restore();
}

function dashed(x, x0, x1, y) {
  x.save();
  x.strokeStyle = '#CFCBC1';
  x.lineWidth = 2;
  x.setLineDash([6, 5]);
  x.beginPath();
  x.moveTo(x0, y);
  x.lineTo(x1, y);
  x.stroke();
  x.restore();
}

// The member's graph: People, Power and Influence, as on their page
function drawGraph(x, ppi, left, top, width, height, cycle = null) {
  const base = top + height;
  const pts = PARTS.map((p, i) => ({
    ...p,
    value: ppi[p.key] ?? 0,
    x: left + (width * (2 * i + 1)) / 6,
    y: top + (1 - Math.max(0, Math.min(100, ppi[p.key] ?? 0)) / 100) * height,
  }));
  const curve = monotonePath(pts);
  const line = new Path2D(curve);
  const area = new Path2D(`${curve} L${pts[2].x},${base} L${pts[0].x},${base} Z`);
  x.save();
  x.strokeStyle = '#DDDAD2';
  x.lineWidth = 1;
  x.beginPath();
  x.moveTo(left, base + 0.5);
  x.lineTo(left + width, base + 0.5);
  x.stroke();
  // The site's graph colours, people (left) to corporate (right)
  const paint = x.createLinearGradient(pts[0].x, 0, pts[2].x, 0);
  GRAPH_GRADIENT.forEach((c, i) => paint.addColorStop(i / (GRAPH_GRADIENT.length - 1), c));
  x.globalAlpha = 0.22;
  x.fillStyle = paint;
  x.fill(area);
  x.globalAlpha = 1;
  x.strokeStyle = paint;
  x.lineWidth = 3;
  x.lineJoin = 'round';
  // The line stops where the shape does
  x.save();
  x.beginPath();
  x.rect(pts[0].x, 0, pts[2].x - pts[0].x, base + 2);
  x.clip();
  x.stroke(line);
  x.restore();
  // The election cycle, as on the page: whose cycle the figures are
  x.fillStyle = '#3D3A46';
  x.textAlign = 'right';
  x.font = `700 11px ${MONO}`;
  x.fillText(cycleShort(cycle ?? undefined), left + width, top + 8);
  for (const p of pts) {
    x.fillStyle = '#3D3A46';
    x.textAlign = 'center';
    x.font = `600 11px ${MONO}`;
    x.fillText(p.label.toUpperCase(), p.x, base + 18);
    x.fillStyle = '#15131C';
    x.font = `600 14px ${MONO}`;
    x.fillText(`${p.value}%`, p.x, base + 36);
  }
  x.restore();
}

function drawReceipt(x, card) {
  const w = 330;
  const lines = card.lines.slice(0, 4);
  // The graph, and room under it so the stamp only ever covers the barcode
  const graph = card.ppi ? 136 : 0;
  const h = 214 + lines.length * 34 + graph;
  x.save();
  x.translate(64 + w / 2, 60 + h / 2);
  x.rotate((-3 * Math.PI) / 180);
  x.translate(-w / 2, -h / 2);
  x.shadowColor = 'rgba(0,0,0,0.45)';
  x.shadowBlur = 50;
  x.shadowOffsetY = 26;
  x.fillStyle = '#FFFEFA';
  x.beginPath();
  x.roundRect(0, 0, w, h, 4);
  x.fill();
  x.shadowColor = 'transparent';

  x.fillStyle = '#15131C';
  x.textAlign = 'center';
  x.font = `600 14px ${MONO}`;
  x.fillText('TASK FORCE PURPLE', w / 2, 38);
  x.fillStyle = '#55525E';
  x.font = `400 11px ${MONO}`;
  // How recent the money is, when the filings date is known
  x.fillText(
    card.filedOn
      ? `RECEIPT · FEC FILINGS TO ${card.filedOn.toUpperCase()}`
      : `RECEIPT · ${card.cycleLabel}`,
    w / 2,
    56
  );
  dashed(x, 24, w - 24, 74);

  x.textAlign = 'left';
  let y = 104;
  for (const l of lines) {
    x.fillStyle = l.canvas;
    x.fillRect(24, y - 10, 9, 9);
    x.fillStyle = '#15131C';
    x.font = `400 14px ${MONO}`;
    x.fillText(l.short, 42, y);
    const labelEnd = 42 + x.measureText(l.short).width + 8;
    x.font = `600 14px ${MONO}`;
    // Dollars, as on the page's receipt: the graph has the shares
    const value = usdShort(l.amount);
    const vw = x.measureText(value).width;
    x.fillText(value, w - 24 - vw, y);
    dotted(x, labelEnd, w - 24 - vw - 8, y);
    y += 34;
  }
  dashed(x, 24, w - 24, y - 14);
  x.font = `600 17px ${MONO}`;
  x.fillText('TOTAL', 24, y + 14);
  const total = card.total;
  x.fillText(total, w - 24 - x.measureText(total).width, y + 14);

  if (card.ppi) {
    drawGraph(x, card.ppi, 24, y + 34, w - 48, 54, card.cycle);
    y += graph;
  }
  // a barcode from the person's ID, for the look of the thing
  let cx = 24;
  x.fillStyle = '#15131C';
  for (const ch of card.id.repeat(3)) {
    const bar = 1 + (ch.charCodeAt(0) % 4);
    if (cx + bar > w - 24) {
      break;
    }
    x.fillRect(cx, y + 64, bar, 28);
    cx += bar + 3;
  }
  if (card.boosted) {
    drawBoosted(x, w * 0.36, y + 78);
  }
  x.restore();
  return 60 + h;
}

// BOOSTED (owner, 2026-10-25): red ink across the receipt's foot, over the
// barcode and clear of the grade stamp, never over a figure
const BOOSTED_RED = '#C8102E';
function drawBoosted(x, cx, cy) {
  // Drawn on its own canvas and worn there, so the holes are in the ink and
  // not in the receipt under it; then laid on like ink
  const W0 = 210;
  const H0 = 70;
  const ink = document.createElement('canvas');
  ink.width = W0;
  ink.height = H0;
  const k = ink.getContext('2d');
  k.translate(W0 / 2, H0 / 2);
  k.strokeStyle = BOOSTED_RED;
  k.lineWidth = 4;
  k.beginPath();
  k.roundRect(-95, -25, 190, 50, 5);
  k.stroke();
  k.lineWidth = 1.5;
  k.beginPath();
  k.roundRect(-88, -18, 176, 36, 3);
  k.stroke();
  k.fillStyle = BOOSTED_RED;
  k.textAlign = 'center';
  stretch(k, 'condensed');
  k.font = `900 32px ${DISPLAY}`;
  k.fillText('BOOSTED', 0, 12);
  // Worn ink: small specks taken out, the same every time (a fixed seed)
  k.setTransform(1, 0, 0, 1, 0, 0);
  k.globalCompositeOperation = 'destination-out';
  let seed = 7;
  const rand = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < 900; i++) {
    k.globalAlpha = 0.3 + rand() * 0.7;
    k.fillRect(rand() * W0, rand() * H0, 1 + rand() * 1.6, 1 + rand() * 1.6);
  }
  x.save();
  x.translate(cx, cy);
  x.rotate((-9 * Math.PI) / 180);
  x.globalAlpha = 0.85;
  x.globalCompositeOperation = 'multiply';
  x.drawImage(ink, -W0 / 2, -H0 / 2);
  x.restore();
}

function drawStamp(x, g, cx, cy) {
  x.save();
  x.translate(cx, cy);
  x.rotate((-12 * Math.PI) / 180);
  x.beginPath();
  x.arc(0, 0, 84, 0, Math.PI * 2);
  x.fillStyle = 'rgba(255,254,250,0.92)';
  x.fill();
  x.lineWidth = 8;
  x.strokeStyle = g.color;
  x.stroke();
  x.fillStyle = g.color;
  x.textAlign = 'center';
  stretch(x, 'normal');
  x.font = `900 96px ${DISPLAY}`;
  x.fillText(g.mark, 0, 30);
  x.font = `600 13px ${MONO}`;
  const word = g.name.toUpperCase();
  if (x.measureText(word).width <= 130 || !word.includes(' ')) {
    x.fillText(word, 0, 54);
  } else {
    // "HEAVILY INFLUENCED": two lines, as on the page's stamp
    x.font = `600 12px ${MONO}`;
    const cut = word.lastIndexOf(' ');
    x.fillText(word.slice(0, cut), 0, 50);
    x.fillText(word.slice(cut + 1), 0, 65);
  }
  x.restore();
}

/**
 * card: { id, name, title, tier, filedOn ('Sep 30, 2026'), lines: [{short, amount, canvas}], ppi (People,
 *         Power, Influence), total, headline, sub, host, cycleLabel, boosted }
 */
export async function drawShareCard(card) {
  await fontsReady();
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const x = canvas.getContext('2d');
  const g = gradeInfo(card.tier);

  // Red into blue, as on the site
  const bg = x.createLinearGradient(0, 0, W, H * 0.4);
  bg.addColorStop(0, '#B4233C');
  bg.addColorStop(0.52, '#5B21B6');
  bg.addColorStop(1, '#1E4FD0');
  x.fillStyle = bg;
  x.fillRect(0, 0, W, H);
  const receiptBottom = drawReceipt(x, card);
  // Over the receipt's barcode corner: it must never cover a figure
  drawStamp(x, g, 350, receiptBottom - 18);

  const left = 470;
  const width = W - left - 64;
  x.textAlign = 'left';
  x.fillStyle = '#DDD3FE';
  const eyebrow = `${card.title} ${card.name} · grade ${g.mark}`.toUpperCase();
  const eb = fitText(x, eyebrow, {
    width,
    maxLines: 1,
    start: 18,
    min: 12,
    font: s => `500 ${s}px ${MONO}`,
  });
  x.fillText(eb.lines[0], left, 92);

  x.fillStyle = '#FFFFFF';
  stretch(x, 'extra-condensed');
  const head = fitText(x, card.headline.toUpperCase(), {
    width,
    maxLines: 3,
    start: 104,
    min: 44,
    font: s => `900 ${s}px ${DISPLAY}`,
  });
  let y = 108 + head.size * 0.9;
  for (const line of head.lines) {
    x.fillText(line, left, y);
    y += head.size * 0.88;
  }
  stretch(x, 'normal');

  x.fillStyle = '#EDE9F6';
  x.font = `400 24px ${BODY}`;
  // Never into the scope line and the brand line at the foot
  const room = Math.max(0, Math.floor((H - 132 - (y + 14)) / 32) + 1);
  const sub = wrap(x, card.sub, width).slice(0, Math.min(2, room));
  y += 14;
  for (const line of sub) {
    x.fillText(line, left, y);
    y += 32;
  }

  // What a grade is and isn't, so the card can't read as a verdict
  x.fillStyle = '#DDD3FE';
  x.font = `400 17px ${BODY}`;
  x.fillText(GRADE_SCOPE, left, H - 104);

  // brand and address
  x.fillStyle = '#FFFFFF';
  x.beginPath();
  x.arc(left + 18, H - 61, 18, 0, Math.PI * 2);
  x.fill();
  x.fillStyle = '#5B21B6';
  x.textAlign = 'center';
  stretch(x, 'condensed');
  x.font = `900 13px ${DISPLAY}`;
  x.fillText('TFP', left + 18, H - 56);
  stretch(x, 'normal');
  x.textAlign = 'left';
  x.fillStyle = '#FFFFFF';
  stretch(x, 'semi-expanded');
  x.font = `800 20px ${DISPLAY}`;
  x.fillText('TASK FORCE PURPLE', left + 48, H - 54);
  stretch(x, 'normal');
  x.font = `600 22px ${MONO}`;
  x.textAlign = 'right';
  x.fillText(card.host, W - 64, H - 54);
  return canvas;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('No image'))), 'image/png')
  );
}
